import clsx from "clsx";

import { DateTimeTz, Icon } from "@web-app/components";
import { OrderCode, OrderTimeline } from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";
import { codeFormatLabel } from "@web-app/utils/scannableCode";

import { CODE_ENTITY_STOPS } from "./codeEntities";

type CodeActivityProps = {
  item: OrderTimeline;
  showTimelineBar: boolean;
  timezone: keyof typeof timeZoneMap | undefined;
};

function parseCode(value: string): OrderCode | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as OrderCode;
  } catch {
    return null;
  }
}

const describe = (code: OrderCode) =>
  [
    `×${code.quantity}`,
    code.isRequired ? "required" : null,
    code.format ? codeFormatLabel(code.format) : null,
  ]
    .filter(Boolean)
    .join(", ");

function describeChanges(prev: OrderCode, next: OrderCode) {
  const changes: string[] = [];
  if (prev.quantity !== next.quantity) {
    changes.push(`quantity ${prev.quantity} → ${next.quantity}`);
  }
  if (prev.isRequired !== next.isRequired) {
    changes.push(next.isRequired ? "now required" : "no longer required");
  }
  if (prev.format !== next.format) {
    changes.push(
      `format ${codeFormatLabel(prev.format)} → ${codeFormatLabel(next.format)}`,
    );
  }
  return changes.join(", ");
}

const ACTIONS = {
  CREATE: { color: "text-green-600", icon: "fasPlus", text: "Added" },
  DELETE: { color: "text-red-600", icon: "fasTrash", text: "Removed" },
  UPDATE: { color: "text-blue-600", icon: "fasPenField", text: "Updated" },
} as const;

/** A code added to, changed on or removed from a stop ("pickupCode", "dropoffCode"). */
export default function CodeActivity({
  item,
  showTimelineBar,
  timezone,
}: CodeActivityProps) {
  const action = ACTIONS[item.action as keyof typeof ACTIONS] ?? ACTIONS.UPDATE;
  const stop = CODE_ENTITY_STOPS[item.entity];
  const prev = parseCode(item.previousValue);
  const next = parseCode(item.updatedValue);
  const code = next?.code ?? prev?.code ?? "";

  const summary =
    item.action === "CREATE" && next
      ? describe(next)
      : item.action === "DELETE" && prev
        ? describe(prev)
        : prev && next
          ? describeChanges(prev, next)
          : "";

  return (
    <li className="relative flex gap-x-2 md:gap-x-4">
      <div
        className={clsx(
          showTimelineBar ? "-bottom-6" : "h-6",
          "absolute left-0 top-0 flex w-6 justify-center",
        )}
      >
        <div className="w-px bg-gray-200" />
      </div>
      <div className="relative flex h-7 w-6 flex-none items-center justify-center bg-white">
        <Icon name={action.icon} className={clsx("h-3 md:h-4", action.color)} />
      </div>
      <p className="flex-auto flex flex-col items-start py-0.5 text-[10px] md:text-xs leading-0 md:leading-5 text-gray-500">
        <span className={clsx("font-medium", action.color)}>
          {action.text} {stop} barcode <span className="font-mono">{code}</span>
          {summary && <span className="font-normal"> ({summary})</span>}
        </span>
        <span className="relative">
          <DateTimeTz
            start={new Date(item.createdAt)}
            timezone={timezone}
            createdBy={item.createdBy?.name}
            presentation="timeline"
          />
        </span>
      </p>
    </li>
  );
}
